import React, { useEffect } from "react";
import { useDispatch, useSelector } from "../../store";
import {
  addJobHours,
  getCompleteJobs,
  getJobById,
  getJobCount,
  getOpenJobs,
  resetJob,
  resetJobChildren,
  setCategories,
  updateJob,
} from "../../slices/job";
import { Box } from "@mui/material";
import OpenJobs from "../views/Home/OpenJobs";
import { Base } from "../base";
import { useNavigate, useParams } from "react-router-dom";
import {
  resetJobAttachments,
  setTempAttachments,
} from "../../slices/attachment";
import SearchParams, { defaultParams } from "../../types/searchParams";
import { searchJobStatus } from "../../slices/jobStatus";
import AddHoursVM from "../../types/addHoursVM";
import { searchCategories } from "../../slices/category";
import { getSimpleUsers } from "../../slices/user";
import { getJobTypeId, setCurrentViewJobType } from "../../slices/jobType";

interface Props {}

const HomePage: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { currentViewJobType } = useSelector((store) => store.jobType);
  const { categories, openJobsList, completeJobsList, job } = useSelector(
    (store) => store.job,
  );

  useEffect(() => {
    if (id) {
      dispatch(getOpenJobs(+id!)).then((result) => {
        if (result.meta.requestStatus !== "fulfilled") {
          navigate("home");
        }
      });
      dispatch(getCompleteJobs(+id!));
      const filters: string[] = [];
      filters.push(`jobtype:${+id!}`);
      const searchParams: SearchParams = {
        ...defaultParams,
        filters: filters,
        orderBy: "name",
        orderDirection: "Ascending",
      };
      dispatch(searchJobStatus(searchParams));
    }
    dispatch(getJobCount());
    dispatch(getSimpleUsers(true));
    if (currentViewJobType?.id !== +id!) {
      dispatch(getJobTypeId(+id!)).then(({ meta, payload }) => {
        if (meta.requestStatus === "fulfilled") {
          dispatch(setCurrentViewJobType(payload));
        }
      });
    }
  }, [dispatch, currentViewJobType, id]);

  const handleClickJob = async (id?: number) => {
    handleReset();
    navigate(`/job${+(id ?? 0) > 0 ? "/view/" + id : ""}`);
    if (!id) {
      dispatch(
        updateJob({
          ...job,
          answers: currentViewJobType?.questions.map((q) => {
            return { id: 0, question: q, value: "" };
          }),
          jobType: currentViewJobType!,
        }),
      );
    }
  };

  const handleClickViewJob = (id?: number) => {
    handleReset();
    navigate(`/job/view${+(id ?? 0) > 0 ? "/" + id : ""}`);
  };

  const handleReset = () => {
    dispatch(resetJob());
    dispatch(resetJobAttachments());
    dispatch(setCategories([]));
    dispatch(setTempAttachments([]));
    dispatch(resetJobChildren());
  };

  const handleApiSearch = (
    api: "site" | "jobType" | "category" | "problemCode" | "equipment",
    search: SearchParams,
    filter?: "jobType" | "category",
  ) => {
    let filters: string[] = [];
    if (filter === "jobType") {
      filters.push(`jobtype:${currentViewJobType?.id}`);
    }
    if (filter === "category") {
      categories.forEach((x) => {
        filters.push(`category:${x.id ?? ""}`);
      });
    }

    let searchParams = { ...search, filters: filters };
    switch (api) {
      case "category":
        return dispatch(searchCategories(searchParams));
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Base.TitleBar title="Home" />
      <OpenJobs
        onClickJob={handleClickJob}
        onClickViewJob={handleClickViewJob}
        onClickNewJob={handleClickJob}
        apiGetAllUsers={() => dispatch(getSimpleUsers(true))}
        apiCategorySearch={(search: SearchParams) =>
          handleApiSearch("category", search, "jobType")
        }
        apiAddHours={(hours: AddHoursVM) => {
          dispatch(addJobHours(hours));
        }}
        apiGetJob={(id) => {
          dispatch(getJobById(id));
        }}
        openJobsList={openJobsList}
        completeJobsList={completeJobsList}
      />
    </Box>
  );
};

export default HomePage;
